import React, { useState, useEffect } from 'react';
import { Copy, Link as LinkIcon, Lock, X } from 'lucide-react';
import * as analytics from '../services/analytics';
import api from '../services/api';
import CreateExamModal from './CreateExamModal';
import { useToast } from './ui/Toast';
import { copyToClipboard } from '../utils/clipboard';

const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL || window.location.origin;

const FloatingButton: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [examLink, setExamLink] = useState<string | null>(null);
  const [examPassword, setExamPassword] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const handler = () => setIsModalOpen(true);
    window.addEventListener('open-create-exam', handler);
    return () => window.removeEventListener('open-create-exam', handler);
  }, []);

  const handleCreateExam = async (data: {
    tab: string;
    topic?: string;
    sourceUrl?: string;
    pdfFile?: File;
    youtubeUrl?: string;
    questionCount: number;
    timerSeconds: number;
    bankId?: string;
    questionIds?: string[];
    randomCount?: number;
    extraAiTopic?: string;
    extraAiCount?: number;
  }) => {
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');

    if (!token || !userId) {
      toast('You need to be logged in to create an exam.', 'error');
      return;
    }

    setLoading(true);

    try {
      let response;

      if (data.tab === 'bank') {
        response = await api.post(
          `/exams/create-from-bank`,
          {
            userId,
            bankId: data.bankId,
            questionIds: data.questionIds,
            randomCount: data.randomCount,
            extraAiTopic: data.extraAiTopic,
            extraAiCount: data.extraAiCount,
            timerSeconds: data.timerSeconds,
          }
        );
      } else if (data.tab === 'pdf' && data.pdfFile) {
        const formData = new FormData();
        formData.append('pdf', data.pdfFile);
        formData.append('userId', userId);
        formData.append('questionCount', data.questionCount.toString());
        formData.append('timerSeconds', data.timerSeconds.toString());

        response = await api.post(`/exams/create-from-pdf`, formData);
      } else if (data.tab === 'youtube') {
        response = await api.post(
          `/exams/create-from-youtube`,
          { userId, youtubeUrl: data.youtubeUrl, questionCount: data.questionCount, timerSeconds: data.timerSeconds }
        );
      } else if (data.tab === 'url') {
        response = await api.post(
          `/exams/create-from-url`,
          { userId, sourceUrl: data.sourceUrl, questionCount: data.questionCount, timerSeconds: data.timerSeconds }
        );
      } else {
        response = await api.post(
          `/exams/create`,
          { userId, topic: data.topic, questionCount: data.questionCount, timerSeconds: data.timerSeconds }
        );
      }

      const { url, password } = response.data;
      setExamLink(`${FRONTEND_URL}/exam/${url}`);
      setExamPassword(password);
      analytics.track('exam_created', {
        source: data.tab,
        questionCount: data.questionCount,
        timerSeconds: data.timerSeconds,
      });
      toast('Exam created successfully!', 'success');
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { message?: string } } };
      const message = axiosError.response?.data?.message || 'An error occurred while creating the exam.';
      toast(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (examLink) {
      const ok = await copyToClipboard(examLink);
      toast(ok ? 'Link copied!' : 'Failed to copy', ok ? 'success' : 'error');
    }
  };

  const handleCopyPassword = async () => {
    if (examPassword) {
      const ok = await copyToClipboard(examPassword);
      toast(ok ? 'Password copied!' : 'Failed to copy', ok ? 'success' : 'error');
    }
  };

  return (
    <>
      {loading && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 shadow-2xl flex flex-col items-center gap-4 animate-fade-in">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-gray-700 font-medium">Generating your exam...</p>
            <p className="text-gray-400 text-sm">This may take a few seconds</p>
          </div>
        </div>
      )}

      {isModalOpen && (
        <CreateExamModal
          onClose={() => setIsModalOpen(false)}
          onCreate={handleCreateExam}
        />
      )}

      {examLink && examPassword && (
        <div className="fixed bottom-24 right-6 bg-white shadow-2xl rounded-xl p-5 max-w-sm border animate-slide-up z-40">
          <button
            onClick={() => { setExamLink(null); setExamPassword(null); }}
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition"
          >
            <X size={16} />
          </button>

          <h3 className="font-semibold text-gray-800 mb-3">Exam Ready!</h3>

          <div className="space-y-3">
            <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-3">
              <LinkIcon size={16} className="text-gray-400 flex-shrink-0" />
              <a href={examLink} target="_blank" rel="noopener noreferrer" className="text-primary text-sm truncate hover:underline flex-1">
                {examLink}
              </a>
              <button onClick={handleCopyLink} className="text-gray-400 hover:text-primary transition flex-shrink-0" title="Copy link">
                <Copy size={16} />
              </button>
            </div>

            <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-3">
              <Lock size={16} className="text-gray-400 flex-shrink-0" />
              <span className="font-mono text-sm font-bold tracking-wider flex-1">{examPassword}</span>
              <button onClick={handleCopyPassword} className="text-gray-400 hover:text-primary transition flex-shrink-0" title="Copy password">
                <Copy size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingButton;
